import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { EquipmentItem } from "@/components/equipment-item";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { DatePickerInput } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  assetToRNFile,
  formatBytesToMB,
  imagePickerPresets,
  normalizeImageAsset,
  pickImageFromLibrary,
  validateFileSize,
  type ImagePickerAsset,
} from "@/lib/image-picker";
import driverServices from "@/lib/services/drivers";
import { showToast } from "@/lib/toast";
import {
  DriverFormData,
  UpdateDriverFormData,
  driverSchema,
  updateDriverSchema,
} from "@/lib/validation/driver";
import { MAX_UPLOAD_SIZE } from "@/lib/validation/file";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import { Stack, router } from "expo-router";
import React, { useRef, useState } from "react";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import {
  Keyboard,
  KeyboardAvoidingView,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { HelperText } from "react-native-paper";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

export default function LicenseScreen() {
  const insets = useSafeAreaInsets();
  const desktop = useDesktopLayout();
  const { user, setUser, refreshUser, theme } = useAuth();
  const [loading, setLoading] = useState(false);
  const [driverLicence, setDriverLicence] = useState<ImagePickerAsset | null>(
    null,
  );
  const isUpdate = Boolean(user?.driver?.id);
  const scrollRef = useRef<ScrollView>(null);
  const licenseNumberInputRef = useRef<TextInput>(null);
  const licenseNumberOffsetY = useRef(0);

  const formSchema = isUpdate ? updateDriverSchema : driverSchema;
  type formData = UpdateDriverFormData | DriverFormData;

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<formData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: user?.driver?.company_name || "",
      legal_name: user?.driver?.legal_name || user?.driver?.company_name || "",
      trading_name:
        user?.driver?.trading_name || user?.driver?.company_name || "",
      country: user?.driver?.country || "GB",
      company_registration_number:
        user?.driver?.company_registration_number || "",
      vat_status: user?.driver?.vat_status || "NOT_VAT_REGISTERED",
      vat_number: user?.driver?.vat_number || "",
      vat_effective_date: user?.driver?.vat_effective_date
        ? new Date(user.driver.vat_effective_date)
        : undefined,
      license_number: user?.driver?.license_number || "",
      license_expiry_date: user?.driver?.license_expiry_date
        ? new Date(user?.driver?.license_expiry_date)
        : undefined,
      document: undefined,
    },
  });

  const licenseDate = watch("license_expiry_date");
  const vatStatus = watch("vat_status");

  const onSubmit = async (data: formData) => {
    if (!isDirty) {
      router.back();
      return;
    }

    if (!isUpdate) {
      try {
        data.license_expiry_date = new Date(data.license_expiry_date)
          .toISOString()
          .split("T")[0] as unknown as Date;
        if (data.vat_effective_date) {
          data.vat_effective_date = new Date(data.vat_effective_date)
            .toISOString()
            .split("T")[0] as unknown as Date;
        }
        await driverServices.createDriver(data as DriverFormData);
        showToast.success("Driver license successfully");
        // Navigate back after a short delay
        setTimeout(() => {
          router.back();
        }, 1500);
      } catch (error: any) {
        showToast.error(
          error.response?.data?.message || "Failed to update driver license",
        );
      }
    } else {
      if (user?.driver?.id) {
        try {
          const response = await driverServices.updateDriver(
            user?.driver?.id!,
            data,
          );
          setUser({ ...user, driver: { ...user.driver, ...response } });
          showToast.success("Driver license successfully");
          // Navigate back after a short delay
          setTimeout(() => {
            router.back();
          }, 1500);
        } catch (error: any) {
          showToast.error(
            error.response?.data?.message || "Failed to update driver license",
          );
        }
      }
    }
  };

  const onInvalid = (formErrors: FieldErrors<formData>) => {
    if (!formErrors.license_number) {
      return;
    }

    scrollRef.current?.scrollTo({
      y: Math.max(0, licenseNumberOffsetY.current - 16),
      animated: true,
    });
  };

  const handlePick = async () => {
    const asset = await pickImageFromLibrary(imagePickerPresets.document);
    if (!asset) {
      return;
    }

    const normalizedAsset = await normalizeImageAsset(asset);
    const { isValid, message } = validateFileSize(normalizedAsset);
    if (!isValid) {
      showToast.error(
        message || "Failed to update profile picture. File size is too large.",
      );
      return;
    }

    setDriverLicence(normalizedAsset);
    const newFile = assetToRNFile(normalizedAsset, "driver_license");

    if (user?.driver?.id) {
      await driverServices.uploadDocument(
        Number(user?.driver?.id),
        "driver_license",
        normalizedAsset,
      );
      showToast.success("Document uploaded successfully");
      setValue("document", newFile);
    } else {
      setValue("document", newFile);
    }
  };

  const handleReupload = async (type: string) => {
    try {
      const asset = await pickImageFromLibrary(imagePickerPresets.document);
      if (!asset) {
        return;
      }

      const normalizedAsset = await normalizeImageAsset(asset);
      const { isValid, message } = validateFileSize(normalizedAsset);
      if (!isValid) {
        showToast.error(
          message || "Failed to re-upload document. File size is too large.",
        );
        return;
      }

      setLoading(true);
      await driverServices.uploadDocument(
        Number(user?.driver?.id),
        type,
        normalizedAsset,
      );
      showToast.success("Document re-uploaded successfully");
      await refreshUser();
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to upload document",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <DesktopAppFrame>
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top", "bottom"]}
    >
      <Stack.Screen options={{ headerShown: false }} />
      <ScreenHeader title="Driver&apos;s licence" showMenu={false} />
      {(loading || isSubmitting) && <Loader />}
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={[
            styles.scrollContent,
            desktop && styles.desktopContent,
          ]}
          showsVerticalScrollIndicator={false}
          automaticallyAdjustKeyboardInsets={true}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="interactive"
        >
          {user?.driver?.document && !user?.driver?.document?.is_verified && (
            <View style={styles.reviewBanner}>
              <Ionicons name="time" size={24} color="#FF9500" />
              <ThemedText style={styles.reviewBannerText} type="small">
                This driver license is currently under review. You&apos;ll be
                notified once it&apos;s verified.
              </ThemedText>
            </View>
          )}
          <View
            style={[styles.groupHeader, { borderTopColor: theme.border }]}
          >
            <ThemedText style={styles.groupTitle} type="subtitle">
              Driving details
            </ThemedText>
            <ThemedText
              type="extraSmall"
              lightColor={BrandColors.darkGray}
              darkColor={BrandColors.mediumGray}
            >
              Required for verification
            </ThemedText>
          </View>
          <View>
            {user?.driver?.document ? (
              <View style={styles.section}>
                <Label label="Driver&apos;s License Photo" style={styles.label} />
                <View
                  style={[styles.card, { backgroundColor: theme.background }]}
                >
                  <View
                    key={user?.driver?.document?.id}
                    style={[styles.documentItem, { borderBottomWidth: 0 }]}
                  >
                    <View
                      style={[
                        styles.documentHeader,
                        { borderColor: theme.border },
                      ]}
                    >
                      <View style={styles.documentInfo}>
                        <Ionicons name="image" size={24} color={theme.text} />
                        <View>
                          <ThemedText
                            style={styles.documentType}
                            type="smallSemiBold"
                          >
                            {user?.driver?.document?.type
                              .replace("_", " ")
                              .toUpperCase()}
                          </ThemedText>
                          <ThemedText
                            style={styles.documentDate}
                            type="extraSmall"
                          >
                            Uploaded on{" "}
                            {new Date(
                              user?.driver?.document?.created_at,
                            ).toLocaleDateString()}
                          </ThemedText>
                          <TouchableOpacity
                            onPress={() =>
                              Linking.openURL(user?.driver?.document?.url!)
                            }
                            style={styles.viewLink}
                          >
                            <ThemedText
                              style={styles.viewLinkText}
                              type="extraSmallSemiBold"
                            >
                              View Document
                            </ThemedText>
                          </TouchableOpacity>
                        </View>
                      </View>
                      <View
                        style={[
                          styles.docStatusBadge,
                          user?.driver?.document?.is_verified
                            ? styles.docVerifiedBadge
                            : styles.docPendingBadge,
                        ]}
                      >
                        <ThemedText
                          style={[
                            styles.docStatusText,
                            user?.driver?.document?.is_verified
                              ? styles.docVerifiedText
                              : styles.docPendingText,
                          ]}
                          type="extraSmallSemiBold"
                        >
                          {user?.driver?.document?.is_verified
                            ? "Verified"
                            : "Pending"}
                        </ThemedText>
                      </View>
                    </View>

                    {!user?.driver?.document?.is_verified &&
                      user?.driver?.document?.rejection_reason && (
                        <View style={styles.rejectionContainer}>
                          <Ionicons
                            name="alert-circle"
                            size={16}
                            color="#FF3B30"
                          />
                          <ThemedText
                            style={styles.rejectionText}
                            type="extraSmall"
                          >
                            Rejected: {user?.driver?.document?.rejection_reason}
                          </ThemedText>
                          <TouchableOpacity
                            onPress={() =>
                              handleReupload(user?.driver?.document?.type!)
                            }
                            style={styles.reuploadButton}
                          >
                            <ThemedText
                              style={styles.reuploadButtonText}
                              type="extraSmallSemiBold"
                            >
                              Re-upload
                            </ThemedText>
                          </TouchableOpacity>
                        </View>
                      )}
                  </View>
                </View>
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={[
                    styles.uploadTile,
                    errors.document && styles.uploadTileError,
                  ]}
                  onPress={handlePick}
                  activeOpacity={0.8}
                >
                  {driverLicence ? (
                    <Image
                      source={driverLicence.uri}
                      style={styles.uploadPreview}
                      contentFit="cover"
                    />
                  ) : (
                    <Ionicons
                      name="share-outline"
                      size={24}
                      color={BrandColors.primary}
                    />
                  )}
                  <View style={styles.uploadTextContainer}>
                    <ThemedText
                      type="smallSemiBold"
                      lightColor={BrandColors.charcoal}
                      darkColor={BrandColors.charcoal}
                    >
                      {driverLicence
                        ? "Driver’s licence selected"
                        : "Upload driver’s licence"}
                    </ThemedText>
                    <ThemedText
                      type="extraSmall"
                      lightColor={BrandColors.darkGray}
                      darkColor={BrandColors.darkGray}
                      numberOfLines={1}
                    >
                      {driverLicence
                        ? "Tap to change"
                        : `JPG or PNG · Max ${formatBytesToMB(MAX_UPLOAD_SIZE, 0)}`}
                    </ThemedText>
                  </View>
                </TouchableOpacity>
                {errors.document && (
                  <HelperText type="error">
                    {errors.document?.message}
                  </HelperText>
                )}
              </>
            )}
          </View>

          <View
            style={styles.section}
            onLayout={(event) => {
              licenseNumberOffsetY.current = event.nativeEvent.layout.y;
            }}
          >
            <Label label="Licence number" style={styles.label} />
            <Controller
              control={control}
              name="license_number"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  ref={licenseNumberInputRef}
                  placeholder="e.g. MORGA657054SM9IJ"
                  value={value}
                  onChangeText={(text) =>
                    onChange(
                      text
                        .replace(/[\s-]+/g, "")
                        .toUpperCase()
                        .slice(0, 16),
                    )
                  }
                  onBlur={onBlur}
                  error={!!errors.license_number}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={16}
                />
              )}
            />
            {errors.license_number && (
              <HelperText type="error">
                {errors.license_number?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <DatePickerInput
              label="Expiry date"
              value={licenseDate}
              onChange={(date) => {
                Keyboard.dismiss();
                setValue("license_expiry_date", date);
              }}
              error={errors.license_expiry_date?.message}
            />
          </View>

          <View
            style={[
              styles.groupHeader,
              styles.businessGroupHeader,
              { borderTopColor: theme.border },
            ]}
          >
            <ThemedText style={styles.groupTitle} type="subtitle">
              Business details
            </ThemedText>
            <ThemedText
              type="extraSmall"
              lightColor={BrandColors.darkGray}
              darkColor={BrandColors.mediumGray}
            >
              Used on invoices and payouts
            </ThemedText>
          </View>

          <View style={styles.section}>
            <Label label="Company name" style={styles.label} />
            <Controller
              control={control}
              name="company_name"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Enter company name"
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={!!errors.company_name}
                />
              )}
            />
            {errors.company_name && (
              <HelperText type="error">
                {errors.company_name?.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Label label="Legal name" style={styles.label} />
            <Controller
              control={control}
              name="legal_name"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Legal entity name"
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={!!errors.legal_name}
                />
              )}
            />
            {errors.legal_name && (
              <HelperText type="error">{errors.legal_name?.message}</HelperText>
            )}
          </View>

          <View style={styles.section}>
            <Label label="Company registration" style={styles.label} />
            <Controller
              control={control}
              name="company_registration_number"
              render={({ field: { onChange, onBlur, value } }) => (
                <Input
                  placeholder="Enter registration number"
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={!!errors.company_registration_number}
                />
              )}
            />
            {errors.company_registration_number && (
              <HelperText type="error">
                {errors.company_registration_number?.message}
              </HelperText>
            )}
          </View>
          <View style={styles.section}>
            <Label label="VAT Status" style={styles.label} />
            <Controller
              control={control}
              name="vat_status"
              render={({ field: { onChange, value } }) => (
                <EquipmentItem
                  label="Are you VAT registered?"
                  value={value === "VAT_REGISTERED"}
                  onValueChange={(isVatRegistered) => {
                    Keyboard.dismiss();
                    onChange(
                      isVatRegistered ? "VAT_REGISTERED" : "NOT_VAT_REGISTERED",
                    );
                    if(!isVatRegistered) {
                      setValue("vat_number", "");
                      setValue("vat_effective_date", undefined);
                    }
                  }}
                  isLast
                />
              )}
            />
            {errors.vat_status && (
              <HelperText type="error">{errors.vat_status?.message}</HelperText>
            )}
          </View>
          {vatStatus === "VAT_REGISTERED" ? (
            <>
              <View style={styles.section}>
                <ThemedText style={styles.label} type="smallSemiBold">
                  VAT Number
                </ThemedText>
                <Controller
                  control={control}
                  name="vat_number"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder="Enter VAT number"
                      value={value}
                      onChangeText={onChange}
                      onBlur={onBlur}
                      error={!!errors.vat_number}
                    />
                  )}
                />
                {errors.vat_number && (
                  <HelperText type="error">
                    {errors.vat_number?.message}
                  </HelperText>
                )}
              </View>
              <View style={styles.section}>
                <DatePickerInput
                  label="VAT Effective Date"
                  value={watch("vat_effective_date")}
                  onChange={(date) => {
                    Keyboard.dismiss();
                    setValue("vat_effective_date", date, { shouldDirty: true });
                  }}
                  error={errors.vat_effective_date?.message}
                />
              </View>
            </>
          ) : null}
        </ScrollView>
      </KeyboardAvoidingView>
      <View
        style={[
          { marginBottom: insets.bottom, paddingHorizontal: 20 },
          desktop && styles.desktopContent,
          desktop && { paddingBottom: 24 },
        ]}
      >
        <BrandButton
          variant="primary"
          onPress={handleSubmit(onSubmit, onInvalid)}
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Saving..."
            : isUpdate
              ? "Save"
              : "Save and continue"}
        </BrandButton>
      </View>
    </SafeAreaView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 10,
    paddingHorizontal: 20,
    paddingBottom: 40,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  section: {
    paddingVertical: 10,
  },
  groupHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    borderTopWidth: 1,
    paddingTop: 20,
    marginTop: 10,
    marginBottom: 16,
  },
  businessGroupHeader: {
    marginTop: 20,
    marginBottom: 6,
  },
  groupTitle: {
    fontSize: 18,
  },
  uploadTile: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    minHeight: 92,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: BrandColors.primary,
    backgroundColor: BrandColors.primaryLight,
    marginBottom: 10,
  },
  uploadTileError: {
    borderColor: BrandColors.error,
  },
  uploadPreview: {
    width: 48,
    height: 48,
    borderRadius: 8,
  },
  uploadTextContainer: {
    flex: 1,
    gap: 2,
  },
  label: {
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  saveButton: {
    borderRadius: 12,
    padding: 16,
    margin: 20,
    alignItems: "center",
  },
  saveButtonDisabled: {
    backgroundColor: "#ccc",
  },
  saveButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  card: {
    borderRadius: 12,
  },
  documentItem: {
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  documentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
  },
  documentInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  documentType: {
    fontSize: 14,
    fontWeight: "bold",
  },
  documentDate: {
    fontSize: 12,
    marginTop: 2,
  },
  docStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  docVerifiedBadge: {
    backgroundColor: "#E8F5E9",
  },
  docPendingBadge: {
    backgroundColor: "#FFF3E0",
  },
  docStatusText: {
    fontSize: 12,
    fontWeight: "600",
  },
  docVerifiedText: {
    color: "#34C759",
  },
  docPendingText: {
    color: "#FF9500",
  },
  rejectionContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    padding: 10,
    backgroundColor: "#FFF5F5",
    borderRadius: 8,
  },
  rejectionText: {
    fontSize: 13,
    color: "#FF3B30",
    flex: 1,
  },
  reuploadButton: {
    backgroundColor: "#FF3B30",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 8,
  },
  reuploadButtonText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "600",
  },
  viewLink: {
    marginTop: 4,
  },
  viewLinkText: {
    fontSize: 13,
    color: "#007AFF",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  reviewBanner: {
    backgroundColor: "#FFF3E0",
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#FFE0B2",
    borderRadius: 12,
  },
  reviewBannerText: {
    flex: 1,
    fontSize: 14,
    color: "#E65100",
    lineHeight: 20,
  },
});
